// The parameter edit of the Variables window (spec 6.10.11): an input typed by the parameter's declaration, a
// minimal merge patch with If-Match, and the 412 rules (design 5.6, B1.8): the deployment's ETag changes on every
// run start and end, so a 412 is common and mostly harmless. Reload; resend silently when the server's value of
// the parameter still equals the value the user started from; otherwise report the conflict and send nothing.
import { isApiError, type ApiClient, type Versioned } from '../api/client';
import type { Deployment, JsonObject, ParameterDefinition, ParameterValue } from '../api/types';
import { deepEqual } from '../lib/json';
import { strings } from '../strings';

const s = strings.variables.parameter;

export type ParameterInput =
  | { kind: 'select'; options: { value: string; label: string }[] }
  | { kind: 'number'; minimum?: number; maximum?: number }
  | { kind: 'text' };

/** The editor's control for a declaration: a select for `allowed` and booleans, a number input, else text. */
export function parameterInput(definition: ParameterDefinition | undefined): ParameterInput {
  const optional = definition?.default !== undefined;
  const empty = optional ? [{ value: '', label: s.useDefault(String(definition!.default)) }] : [];
  if (definition?.allowed?.length)
    return {
      kind: 'select',
      options: [...empty, ...definition.allowed.map((v) => ({ value: String(v), label: String(v) }))],
    };
  if (definition?.type === 'boolean')
    return {
      kind: 'select',
      options: [...empty, { value: 'true', label: 'true' }, { value: 'false', label: 'false' }],
    };
  if (definition?.type === 'number')
    return { kind: 'number', minimum: definition.minimum, maximum: definition.maximum };
  return { kind: 'text' };
}

/** The text the editor starts with: the deployment's value, else the declaration's default, else empty. */
export function initialText(value: ParameterValue | undefined, definition?: ParameterDefinition): string {
  if (value !== undefined) return String(value);
  return definition?.default === undefined ? '' : String(definition.default);
}

/**
 * The editor's text says nothing new: it is still the text it started with (for an unset parameter that is the
 * default, which must not be pinned as an explicit value, code review C19), or it parses to the same value.
 */
export function isUnchangedEdit(
  text: string,
  value: ParameterValue | undefined,
  definition?: ParameterDefinition,
): boolean {
  if (text === initialText(value, definition)) return true;
  const parsed = parseParameterText(definition, text);
  return parsed.ok && sameValue(parsed.value, value);
}

/**
 * The value to save for the editor's text: `null` removes the parameter's value (its default applies). Invalid
 * input is refused in the browser (spec 6.10.11).
 */
export function parseParameterText(
  definition: ParameterDefinition | undefined,
  text: string,
): { ok: true; value: ParameterValue | null } | { ok: false; error: string } {
  if (text === '') {
    return definition?.default !== undefined ? { ok: true, value: null } : { ok: false, error: s.required };
  }
  if (definition?.allowed?.length && !definition.allowed.map(String).includes(text))
    return { ok: false, error: s.notAllowed };
  if (definition?.type === 'number') {
    const value = Number(text);
    if (!Number.isFinite(value) || text.trim() === '') return { ok: false, error: s.notANumber };
    if (
      (definition.minimum !== undefined && value < definition.minimum) ||
      (definition.maximum !== undefined && value > definition.maximum)
    )
      return { ok: false, error: strings.settings.range(definition.minimum, definition.maximum) };
    return { ok: true, value };
  }
  if (definition?.type === 'boolean') {
    if (text !== 'true' && text !== 'false') return { ok: false, error: s.notABoolean };
    return { ok: true, value: text === 'true' };
  }
  return { ok: true, value: text };
}

/** The minimal merge patch for one parameter (`null` removes it). */
export function parameterPatch(name: string, value: ParameterValue | null): JsonObject {
  return { settings: { parameters: { [name]: value } } };
}

/** Equal parameter values; both absent counts as equal. */
export function sameValue(
  a: ParameterValue | null | undefined,
  b: ParameterValue | null | undefined,
): boolean {
  return deepEqual(a ?? null, b ?? null);
}

export type SaveOutcome =
  | { kind: 'saved'; deployment: Versioned<Deployment>; resent: number }
  | {
      kind: 'conflict';
      /** The server's value now (undefined when the deployment has none). */
      current: ParameterValue | undefined;
      deployment: Versioned<Deployment>;
    };

/** At most this many silent resends after 412 (a run starting and ending in between is two). */
export const MAX_RESENDS = 3;

/**
 * Saves one parameter with `If-Match` and the 412 rules: on 412 the deployment is reloaded; when the server's
 * value still equals `base` (the value the user started from) the patch is resent with the new ETag, when it
 * already equals the new value the reload counts as saved, and otherwise the conflict is returned without
 * sending anything. Other errors are thrown.
 */
export async function saveParameter(
  api: Pick<ApiClient, 'patchDeployment' | 'getDeployment'>,
  request: {
    deploymentId: string;
    etag: string;
    name: string;
    base: ParameterValue | undefined;
    value: ParameterValue | null;
  },
): Promise<SaveOutcome> {
  const patch = parameterPatch(request.name, request.value);
  let etag = request.etag;
  for (let resent = 0; ; resent++) {
    try {
      const saved = await api.patchDeployment(request.deploymentId, patch, etag);
      return { kind: 'saved', deployment: saved, resent };
    } catch (error) {
      if (!isApiError(error, 'PRECONDITION_FAILED') || resent >= MAX_RESENDS) throw error;
    }
    const fresh = await api.getDeployment(request.deploymentId);
    const current = fresh.data.settings.parameters?.[request.name];
    if (sameValue(current, request.value)) return { kind: 'saved', deployment: fresh, resent };
    if (!sameValue(current, request.base)) return { kind: 'conflict', current, deployment: fresh };
    etag = fresh.etag;
  }
}

/** "environment was changed to `x` since you opened it." */
export function conflictMessage(name: string, current: ParameterValue | undefined): string {
  return current === undefined ? s.conflictRemoved(name) : s.conflict(name, String(current));
}
